import { fetchAuthSession } from "aws-amplify/auth";
import { generateClient } from 'aws-amplify/api';
import {
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBCardText,
  MDBCol,
  MDBFile,
  MDBIcon,
  MDBInput,
  MDBRow,
  MDBSelect,
  MDBSpinner,
  MDBCheckbox
} from "mdb-react-ui-kit";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { captureError } from '../../utils/sentryHelpers';
import { inviteEmployee } from '../../graphql/mutations';

const client = generateClient();

async function getIdToken() {
  const { idToken } = (await fetchAuthSession()).tokens ?? {};
  return idToken;
}

export default function AddSpeaker(props) {
  const { t } = useTranslation();
  const { companyId, isActive, onSpeakerAdded } = props;

  const [hasSearchedUser, setHasSearchedUser] = useState(false);
  const [searchedUser, setSearchedUser] = useState(null);
  const [addAsEmployee, setAddAsEmployee] = useState(false);

  // Search user by email
  const searchUser = async () => {
    try {
      const user = await client.graphql({
        variables: { filter: { email: { eq: userInput.email } }, limit: 1 },
        query: /* GraphQL */ `
          query SearchUsers(
            $filter: SearchableUserFilterInput
            $sort: [SearchableUserSortInput]
            $limit: Int
            $nextToken: String
            $from: Int
            $aggregates: [SearchableUserAggregationInput]
          ) {
            searchUsers(
              filter: $filter
              sort: $sort
              limit: $limit
              nextToken: $nextToken
              from: $from
              aggregates: $aggregates
            ) {
              nextToken
              items {
                email
                lastName
                middleName
                image {
                  fileName
                  alt
                }
                id
                firstName
                title
                personalDepartment
                personalIndustry
                employeeProfiles(filter: {companyEmployeesId: {eq: "${companyId}"}}) {
                  items {
                    id
                    company {
                      name
                      status
                      id
                    }
                  }
                }
              }
            }
          }
        `
      });

      if (user.data.searchUsers.items.length > 0) {
        setSearchedUser(user.data.searchUsers.items[0]);
        setHasSearchedUser(true);
      } else {
        setSearchedUser(null);
        setHasSearchedUser(true);
      }
    } catch (error) {
      console.log("error", error);
      captureError(error, {
        feature: 'company-management',
        operation: 'search-user',
        context: { email: userInput.email },
        tags: { component: 'AddSpeaker' }
      });
    }
  };

  // Handle click on button
  const handleClick = e => {
    searchUser();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      searchUser();
    }
  };

  useEffect(() => {
    if (!isActive) {
      setHasSearchedUser(false);
      setSearchedUser(null);
      setAddAsEmployee(false);
    }
  }, [isActive]);

  // Add existing User as Speaker (and optionally as Employee)
  const [isLoadingAddExistingUser, setIsLoadingAddExistingUser] = useState(false);
  const addExistingUser = async () => {
    const idToken = await getIdToken();
    setIsLoadingAddExistingUser(true);

    try {
      if (addAsEmployee) {
        // Add as employee first, then as speaker
        const response = await fetch('https://y3vayhw2i5bqrm6fpwqz7fzfwe.appsync-api.eu-central-1.amazonaws.com/graphql', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': idToken
          },
          body: JSON.stringify({
            query: /* GraphQL */ `
              mutation InviteEmployee(
                $userId: String
                $companyId: String
                $departmentId: String
                $role: String
                $existingUser: Boolean
                $newUser: String
                $newEmployee: String
              ) {
                inviteEmployee(
                  userId: $userId
                  companyId: $companyId
                  departmentId: $departmentId
                  role: $role
                  existingUser: $existingUser
                  newUser: $newUser
                  newEmployee: $newEmployee
                )
              }
            `,
            variables: {
              userId: searchedUser.id,
              companyId: companyId,
              role: userInput.role || "employee",
              existingUser: true
            }
          })
        });

        const responseData = await response.json();
        console.log("responseData", responseData);
      }

      // Notify parent component with user data
      if (onSpeakerAdded) {
        onSpeakerAdded({
          id: searchedUser.id,
          firstName: searchedUser.firstName,
          lastName: searchedUser.lastName,
          middleName: searchedUser.middleName,
          title: searchedUser.title,
          email: searchedUser.email,
          image: searchedUser.image,
          speakerType: addAsEmployee ? 'employee' : 'user',
          addedAsEmployee: addAsEmployee
        });
      }

      // Update searched user to show success
      setSearchedUser(old => ({ ...old, added: true }));
    } catch (error) {
      console.log("error", error);
      captureError(error, {
        feature: 'company-management',
        operation: 'add-existing-speaker',
        context: { userId: searchedUser.id, addAsEmployee },
        tags: { component: 'AddSpeaker' }
      });
    }
    setIsLoadingAddExistingUser(false);
  };

  const [userInput, setUserInput] = useState({
    email: "",
    firstName: "",
    title: "",
    salutation: "",
    middleName: "",
    lastName: "",
    position: "",
    role: "",
    image: {
      fileName: "",
      alt: ""
    },
    added: false
  });

  const [isLoadingAddNewUser, setIsLoadingAddNewUser] = useState(false);
  const addNewUser = async () => {
    setIsLoadingAddNewUser(true);

    try {
      let userId = null;
      let employeeId = null; // Declare at function scope

      if (addAsEmployee) {
        // Use inviteEmployee to create user AND employee
        const response = await client.graphql({
          query: inviteEmployee,
          variables: {
            companyId: companyId,
            existingUser: false,
            newUser: JSON.stringify({
              email: userInput.email,
              title: userInput.title || '',
              salutation: userInput.salutation || '',
              firstName: userInput.firstName,
              middleName: userInput.middleName || '',
              lastName: userInput.lastName,
              image: userInput.image.fileName ? userInput.image : { fileName: '', alt: '' }
            }),
            newEmployee: JSON.stringify({
              role: userInput.role || "employee",
              image: userInput.image.fileName ? userInput.image : { fileName: '', alt: '' }
            })
          }
        });
        console.log("inviteEmployee response", JSON.stringify(response, null, 2));
        const responseData = response.data;
        console.log("inviteEmployee responseData", responseData);

        // Check for errors
        if (!responseData?.inviteEmployee) {
          throw new Error('Failed to create user: No response data');
        }

        // Extract userId and employeeId from inviteEmployee response
        const inviteEmployeeResult = responseData.inviteEmployee;

        // The response is now an object with userId and employeeId properties
        if (inviteEmployeeResult && typeof inviteEmployeeResult === 'object') {
          userId = inviteEmployeeResult.userId;
          employeeId = inviteEmployeeResult.employeeId;
          console.log("Extracted from inviteEmployee response:", { userId, employeeId });
        } else if (typeof inviteEmployeeResult === 'string') {
          // Fallback for backward compatibility if the Lambda still returns a string
          try {
            const parsed = JSON.parse(inviteEmployeeResult);
            userId = parsed.userId;
            employeeId = parsed.employeeId;
            console.log("Parsed from string response:", { userId, employeeId });
          } catch (e) {
            console.warn("Could not parse inviteEmployee string response", e);
          }
        }

        // Fallback: Search for the user if we don't have the IDs from the response
        if (!userId || !employeeId) {
          console.log("Searching for newly created user and employee by email (fallback)");
          const userSearchResponse = await client.graphql({
            variables: { filter: { email: { eq: userInput.email } }, limit: 1 },
            query: /* GraphQL */ `
              query SearchUsers(
                $filter: SearchableUserFilterInput
                $limit: Int
              ) {
                searchUsers(filter: $filter, limit: $limit) {
                  items {
                    id
                    email
                    employeeProfiles(filter: {companyEmployeesId: {eq: "${companyId}"}}) {
                      items {
                        id
                      }
                    }
                  }
                }
              }
            `
          });

          const foundUser = userSearchResponse.data.searchUsers.items[0];
          if (foundUser) {
            if (!userId) userId = foundUser.id;
            if (!employeeId && foundUser.employeeProfiles.items.length > 0) {
              employeeId = foundUser.employeeProfiles.items[0].id;
            }
            console.log("Found from search:", { userId, employeeId });
          } else {
            throw new Error('Failed to get user ID after creating employee');
          }
        }
      } else {
        // Use createExternalSpeaker to create user WITHOUT employee profile
        const response = await client.graphql({
          query: /* GraphQL */ `
            mutation CreateExternalSpeaker(
              $email: String!
              $firstName: String!
              $lastName: String!
              $middleName: String
              $title: String
              $salutation: String
              $image: ImageInput
            ) {
              createExternalSpeaker(
                email: $email
                firstName: $firstName
                lastName: $lastName
                middleName: $middleName
                title: $title
                salutation: $salutation
                image: $image
              ) {
                message
                userId
                userExists
                temporaryPassword
              }
            }
          `,
          variables: {
            email: userInput.email,
            firstName: userInput.firstName,
            lastName: userInput.lastName,
            middleName: userInput.middleName || null,
            title: userInput.title || null,
            salutation: userInput.salutation || null,
            image: userInput.image.fileName ? {
              fileName: userInput.image.fileName,
              alt: userInput.image.alt || ''
            } : null
          }
        });

        console.log("createExternalSpeaker responseData", response);

        // Extract userId from response (Lambda returns object directly)
        const result = response.data.createExternalSpeaker;
        userId = result.userId;

        if (!userId) {
          throw new Error('Failed to get user ID from response');
        }
      }

      // Notify parent component with new user data
      if (onSpeakerAdded) {
        onSpeakerAdded({
          id: addAsEmployee && employeeId ? employeeId : userId,
          userId: userId, // Always include userId
          employeeId: employeeId, // Include employeeId if available
          firstName: userInput.firstName,
          lastName: userInput.lastName,
          middleName: userInput.middleName,
          title: userInput.title,
          email: userInput.email,
          image: userInput.image,
          speakerType: addAsEmployee && employeeId ? 'employee' : 'user',
          addedAsEmployee: addAsEmployee,
          isNewUser: true
        });
      }

      // Update user to show that he is added successfully
      setUserInput(old => ({ ...old, added: true }));
    } catch (error) {
      console.log("error", JSON.stringify(error, null, 2));
      captureError(error, {
        feature: 'company-management',
        operation: 'add-new-speaker',
        context: { email: userInput.email, addAsEmployee },
        tags: { component: 'AddSpeaker' }
      });
      alert(t("An error occurred while adding the speaker. Please try again."));
    }
    setIsLoadingAddNewUser(false);
  };

  // User Image
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadingImageError, setUploadingImageError] = useState(null);

  const handleSetImage = (file) => {
    if (file) {
      try {
        setIsUploadingImage(true);
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onerror = error => {
          console.log('Error reader: ', error);
          setUploadingImageError(error);
        }
        reader.onload = async () => {
          try {
            const base64FileBuffer = reader.result.replace(/^data:.+;base64,/, '');
            const response = await client.graphql({
              query: /* GraphQL */ `
                mutation UploadCompanyImage($imageBuffer: String, $imageType: String) {
                  uploadCompanyImage(imageBuffer: $imageBuffer, imageType: $imageType)
                }
              `,
              variables: {
                imageBuffer: base64FileBuffer,
                imageType: "userProfileImage",
              },
            });
            const fileName = response.data.uploadCompanyImage.replace(/{|}|fileName=/g, '');
            setUserInput(old => ({ ...old, image: { fileName, alt: userInput.firstName + " " + userInput.lastName } }));
          } catch (error) {
            console.log("error", error);
            captureError(error, {
              feature: 'company-management',
              operation: 'upload-speaker-image-inner',
              context: { email: userInput.email },
              tags: { component: 'AddSpeaker' }
            });
            setIsUploadingImage(false);
            setUploadingImageError(error.errors[0].message);
          }
        }
        setIsUploadingImage(false);
        setUploadingImageError(null);
      } catch (error) {
        console.log("error", error);
        captureError(error, {
          feature: 'company-management',
          operation: 'upload-speaker-image',
          context: { email: userInput.email },
          tags: { component: 'AddSpeaker' }
        });
        setIsUploadingImage(false);
        setUploadingImageError(error.errors[0].message);
      }
    }
  };

  return (
    <>
      <MDBCardText>
        {t("To add a speaker, please search for the user by email address, then add them to your lecture.")}
      </MDBCardText>
      <MDBRow className="mb-3">
        <MDBCol size="12" className="text-center">
          <MDBInput
            type='email'
            autoComplete="off"
            datapath="email"
            label={t("E-mail address")}
            onChange={e => setUserInput({ ...userInput, email: e.target.value })}
            onKeyDown={handleKeyDown}
          />
          <MDBBtn className="mt-2" onClick={e => handleClick(e)}>{t("Search")}</MDBBtn>
        </MDBCol>
      </MDBRow>

      {hasSearchedUser && (searchedUser ? (
        <>
          <div className="text-center">
            <MDBCard className="text-center shadow-5-strong m-4">
              <MDBCardBody>
                <div className='d-flex align-items-center text-start'>
                  {searchedUser?.image?.fileName ? (
                    <img
                      src={"https://iec-cp-public.s3.eu-central-1.amazonaws.com/userImages/" + searchedUser.image.fileName}
                      className="rounded-circle border"
                      style={{ height: "50px", width: "50px", objectFit: "cover" }}
                      alt={searchedUser.image.alt}
                    />
                  ) : (
                    <MDBIcon
                      className="rounded-circle border"
                      style={{ height: "50px", width: "50px", textAlign: "center", paddingTop: "12px", fontSize: "20px" }}
                      fas
                      icon="user-alt"
                    />
                  )}
                  <div className='ms-3 flex-grow-1'>
                    <p className='fw-bold mb-1'>
                      {searchedUser.title ? searchedUser.title + " " : ""}
                      {searchedUser.firstName} {searchedUser.middleName} {searchedUser.lastName}
                    </p>
                    <p className='text-muted mb-0'>{searchedUser.email}</p>
                  </div>
                </div>

                {/* Show warning if user is already an employee */}
                {searchedUser.employeeProfiles.items.length > 0 ? (
                  <>
                    <hr className="my-3" />
                    <div className="alert alert-warning mb-0" role="alert">
                      <div className="d-flex align-items-start">
                        <MDBIcon fas icon="exclamation-triangle" className="me-3 mt-1" size="lg" />
                        <div className="text-start">
                          <h6 className="alert-heading mb-2">
                            {t("This person is already an employee of your company")}
                          </h6>
                          <p className="mb-2">
                            {t("To add this person as a speaker, please select them from the employee list in the previous dialog.")}
                          </p>
                          <p className="mb-0 small text-muted">
                            <MDBIcon fas icon="info-circle" className="me-1" />
                            {t("This ensures consistent speaker management and prevents duplicate entries.")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Only show add button if NOT an employee */}
                    <hr className="my-3" />
                    <div className="d-flex justify-content-center mb-3">
                      {isLoadingAddExistingUser ? (
                        <span><MDBSpinner role="status" /></span>
                      ) : searchedUser.added ? (
                        <MDBBtn disabled color="success" size="lg" className="px-5">
                          <MDBIcon fas icon="check-circle" className="me-2" />
                          {t("Added")}
                        </MDBBtn>
                      ) : (
                        <MDBBtn color="primary" size="lg" className="px-5" onClick={() => addExistingUser()}>
                          <MDBIcon fas icon="user-plus" className="me-2" />
                          {t("Add as Speaker")}
                        </MDBBtn>
                      )}
                    </div>

                    {/* Checkbox for adding as employee */}
                    <div className="text-start">
                      <MDBCheckbox
                        id="addAsEmployee"
                        label={t("Also add as employee to company")}
                        checked={addAsEmployee}
                        onChange={() => setAddAsEmployee(!addAsEmployee)}
                      />
                      {addAsEmployee && (
                        <MDBSelect
                          className="mt-3"
                          onChange={input => input?.value && setUserInput(old => ({ ...old, role: input.value }))}
                          data={[
                            { text: t("Role"), value: "", disabled: true, defaultSelected: true },
                            { text: t('Admin'), value: "admin" },
                            { text: t('Moderator'), value: "moderator" },
                            { text: t('Author'), value: "writer" },
                            { text: t('Employee'), value: "employee" },
                          ]}
                        />
                      )}
                    </div>
                  </>
                )}
              </MDBCardBody>
            </MDBCard>
          </div>
        </>
      ) : (
        <>
          <h6>{t("User was not found, please add information")}:</h6>
          <div className="text-center">
            <h6>{t("Bild")}</h6>
            <div className="w-100 text-center mb-2 mb-lg-4">
              <img
                src={userInput?.image?.fileName ?
                  "https://iec-cp-public.s3.eu-central-1.amazonaws.com/userImages/" + userInput.image.fileName :
                  "https://placehold.co/400x400"}
                className="img-thumbnail rounded-circle border"
                style={{ height: "150px", objectFit: "cover" }}
                alt='...'
              />
            </div>
            <MDBBtn
              color="primary"
              size="sm"
              className="w-50 mb-2 mb-lg-4"
              onClick={() => document.getElementById("image").click()}
            >
              {t("Upload Image")}
            </MDBBtn>
            <MDBFile className="d-none" id='image' onChange={e => handleSetImage(e.target.files[0])} />
            {isUploadingImage && <span><MDBSpinner role="status" /></span>}
            {uploadingImageError && (
              <span className="mt-3 text-danger fw-bold">
                {uploadingImageError}
              </span>
            )}

            <h5>{t("Name")}</h5>
            <MDBRow className="mb-3">
              <MDBCol size="6" className="mb-2">
                <MDBInput
                  autoComplete="off"
                  datapath="salutation"
                  label={t("Salutation")}
                  onChange={e => setUserInput(old => ({ ...old, salutation: e.target.value }))}
                  value={userInput.salutation}
                />
              </MDBCol>
              <MDBCol size="6" className="mb-2">
                <MDBInput
                  autoComplete="off"
                  datapath="title"
                  label={t("Title")}
                  onChange={e => setUserInput(old => ({ ...old, title: e.target.value }))}
                  value={userInput.title}
                />
              </MDBCol>
              <MDBCol size="12" md="6" className="mb-2">
                <MDBInput
                  autoComplete="off"
                  datapath="firstName"
                  label={t("First Name")}
                  onChange={e => setUserInput(old => ({ ...old, firstName: e.target.value }))}
                  value={userInput.firstName}
                />
              </MDBCol>
              <MDBCol size="12" md="6" className="mb-2">
                <MDBInput
                  autoComplete="off"
                  datapath="lastName"
                  label={t("Last name")}
                  onChange={e => setUserInput(old => ({ ...old, lastName: e.target.value }))}
                  value={userInput.lastName}
                />
              </MDBCol>
            </MDBRow>

            <hr className="my-3" />
            <div className="text-start">
              <MDBCheckbox
                id="addAsEmployeeNew"
                label={t("Also add as employee to company")}
                checked={addAsEmployee}
                onChange={() => setAddAsEmployee(!addAsEmployee)}
              />
              {addAsEmployee && (
                <>
                  <h5 className="mt-3">{t("Position")}</h5>
                  <MDBRow className="mb-3">
                    <MDBCol size="12">
                      <MDBSelect
                        onChange={input => input?.value && setUserInput(old => ({ ...old, role: input.value }))}
                        data={[
                          { text: t("Role"), value: "", disabled: true, defaultSelected: true },
                          { text: t('Admin'), value: "admin" },
                          { text: t('Moderator'), value: "moderator" },
                          { text: t('Author'), value: "writer" },
                          { text: t('Employee'), value: "employee" },
                        ]}
                      />
                    </MDBCol>
                  </MDBRow>
                </>
              )}
            </div>

            {isLoadingAddNewUser ? (
              <span><MDBSpinner role="status" /></span>
            ) : userInput.added ? (
              <MDBBtn disabled color="success" className="mt-2">{t("Added")}</MDBBtn>
            ) : (
              <MDBBtn className="mt-2" onClick={() => addNewUser()}>{t("Add as Speaker")}</MDBBtn>
            )}
          </div>
        </>
      ))}
    </>
  );
}
